import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpInterceptorFn,
    HttpRequest,
} from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { SKIP_CUSTOM_HEADERS } from '@interacta-shared/util';
import { Observable } from 'rxjs';
import { BuildInfo } from '../models/environment-info/environment-info.model';
import { ConfigurationService } from '../services/configuration.service';

export const INTERACTA_USER_AGENT_HEADER_NAME = 'Interacta-User-Agent';

export const INTERACTA_USER_AGENT_CLIENT = new InjectionToken<string>(
    'INTERACTA_USER_AGENT_CLIENT',
);

export function userAgentPlatform(
    userAgent: string | undefined,
): string | undefined {
    if (!userAgent) {
        return undefined;
    }
    const separatorIdx = userAgent.search(/[ ,]/);
    return separatorIdx < 0 ? userAgent : userAgent.slice(0, separatorIdx);
}

function buildInteractaUserAgent(
    client: string,
    buildInfo: BuildInfo | undefined,
    userAgent: string | undefined,
): string {
    const platform = userAgentPlatform(userAgent);
    return [
        `client=${client}`,
        buildInfo && `version=${buildInfo.version}`,
        platform && `platform=${platform}`,
        buildInfo && `hash=${buildInfo.hash}`,
    ]
        .filter(Boolean)
        .join(', ');
}

@Injectable()
export class InteractaUserAgentInterceptor implements HttpInterceptor {
    private readonly configurationService = inject(ConfigurationService);
    private readonly client = inject(INTERACTA_USER_AGENT_CLIENT);

    private completeUserAgent: string | undefined;

    intercept(
        req: HttpRequest<unknown>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        if (req.context.get(SKIP_CUSTOM_HEADERS)) {
            return next.handle(req);
        }

        return next.handle(
            req.clone({
                setHeaders: {
                    [INTERACTA_USER_AGENT_HEADER_NAME]: this.userAgent(),
                },
            }),
        );
    }

    private userAgent(): string {
        if (this.completeUserAgent) {
            return this.completeUserAgent;
        }

        const buildInfo =
            this.configurationService.environmentInfo()?.buildInfo.frontend;
        const userAgent = buildInteractaUserAgent(
            this.client,
            buildInfo,
            globalThis.navigator?.userAgent,
        );
        if (buildInfo) {
            this.completeUserAgent = userAgent;
        }
        return userAgent;
    }
}

export const interactaUserAgentInterceptor: HttpInterceptorFn = (req, next) =>
    inject(InteractaUserAgentInterceptor).intercept(req, { handle: next });
